import React, { useContext, useMemo } from 'react'

import { MessageContainer } from './common'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Modal from 'components/Luxkit/Modal/Modal'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { ModalElementContext } from 'contexts/ModalProvider'
import useExperienceSearch from 'hooks/Experiences/useExperienceSearch'
import { take } from 'lib/array/arrayUtils'
import CarouselCTACard from 'tripPlanner/components/Bookmark/Carousels/CarouselCTACard'
import ExperienceItemCarousel from 'tripPlanner/components/Bookmark/Carousels/ExperienceItemCarousel'
import ExperienceSearchModalCTA from 'tripPlanner/components/TripSummary/CallToActionButtons/Experience/ExperienceSearchModalCTA'
import { SectionIdContext } from 'tripPlanner/contexts/SectionIdProvider'
import { useLEPlaceByCoordinates } from 'tripPlanner/hooks/api/place'

interface Props {
  lat: number
  lng: number
  tripId: string
  showSearch: boolean
}

function NearbyExperiencesModal({ lat, lng, tripId, showSearch }: Props) {
  const modalElement = useContext<any>(ModalElementContext)

  const { data: place } = useLEPlaceByCoordinates(lat, lng)
  const sectionId = useContext(SectionIdContext)

  const [list] = useExperienceSearch({
    placeId: place?.id,
    limit: 0,
  })

  const isLoading = (!list || list.fetching) && !list?.error

  const experienceIds = useMemo(() => (list ? take(list.ids, 3) : []), [list])

  const carouselCtaCard = showSearch ? (
    <CarouselCTACard key="ctaCard">
      <ExperienceSearchModalCTA
        tripId={tripId}
        sectionId={sectionId}
        onClick={modalElement.resolve}
      />
    </CarouselCTACard>
  ) : undefined

  return (
    <Modal title="Recommended Offers" onClose={modalElement.resolve} size="L">
      <VerticalSpacer gap={16}>
        {isLoading && (
          <MessageContainer>
            <BodyText
              align="center"
              colour="neutral-one"
              variant="large"
              weight="bold"
            >
              Finding the best offers for you
            </BodyText>
            <LoadingIndicator inline />
          </MessageContainer>
        )}
        {!isLoading && experienceIds.length === 0 && (
          <MessageContainer>
            <BodyText
              align="center"
              colour="neutral-one"
              variant="large"
              weight="bold"
            >
              Sorry, we can't find any experiences nearby
            </BodyText>
            {showSearch && (
              <ExperienceSearchModalCTA
                tripId={tripId}
                sectionId={sectionId}
                onClick={modalElement.resolve}
              />
            )}
          </MessageContainer>
        )}
        {!isLoading && experienceIds.length > 0 && (
          <ExperienceItemCarousel
            offerIds={experienceIds}
            sectionId={sectionId}
            ctaCard={carouselCtaCard}
          />
        )}
      </VerticalSpacer>
    </Modal>
  )
}

export default NearbyExperiencesModal
